import { useMemo } from 'react';
import { useUnderwritingResults } from './use-underwriting-results';
import { getUnderwritingResultForProduct } from '../utils/get-underwriting-result-for-product';
import type { QuoteWithROI, TraditionalLTCQuoteWithROI } from '@/features/shop-insurance';

interface UseQuoteEligibilityFilterProps {
  adjustedQuotes: QuoteWithROI[];
  filteredTraditionalLTCQuotes: TraditionalLTCQuoteWithROI[];
  clientId: string | undefined;
  selectedTab: ShopInsuranceTab;
  selectedEligibilityFilters: string[];
}

/**
 * Processes underwriting results for quote filtering and counting.
 * Uses the refactored underwriting-eligibility infrastructure.
 */
export function useQuoteEligibilityFilter({
  adjustedQuotes,
  filteredTraditionalLTCQuotes,
  clientId,
  selectedTab,
  selectedEligibilityFilters,
}: UseQuoteEligibilityFilterProps) {
  const { data: underwritingData, isLoading } = useUnderwritingResults(clientId);

  const isJointPolicy = selectedTab === 'joint';

  const underwritingResults = useMemo(() => {
    const results = new Map<string, UnderwritingStatus>();

    if (!underwritingData?.results) {
      return results;
    }

    adjustedQuotes.forEach(quote => {
      const result = getUnderwritingResultForProduct(
        quote.productName,
        underwritingData.results,
        isJointPolicy,
        quote.fundingPolicyType,
      );
      if (result?.status) {
        results.set(quote.insuranceQuoteId, result.status);
      }
    });

    filteredTraditionalLTCQuotes.forEach(quote => {
      const result = getUnderwritingResultForProduct(
        quote.productName,
        underwritingData.results,
        isJointPolicy,
        quote.fundingPolicyType,
      );
      if (result?.status) {
        results.set(quote.traditionalLTCQuoteId, result.status);
      }
    });

    return results;
  }, [underwritingData, adjustedQuotes, filteredTraditionalLTCQuotes, isJointPolicy]);

  const eligibilityCounts = useMemo(
    () => ({
      eligible:
        adjustedQuotes.filter(
          quote => underwritingResults.get(quote.insuranceQuoteId) === 'eligible',
        ).length +
        filteredTraditionalLTCQuotes.filter(
          quote => underwritingResults.get(quote.traditionalLTCQuoteId) === 'eligible',
        ).length,
      unsure:
        adjustedQuotes.filter(quote => underwritingResults.get(quote.insuranceQuoteId) === 'unsure')
          .length +
        filteredTraditionalLTCQuotes.filter(
          quote => underwritingResults.get(quote.traditionalLTCQuoteId) === 'unsure',
        ).length,
      ineligible:
        adjustedQuotes.filter(
          quote => underwritingResults.get(quote.insuranceQuoteId) === 'ineligible',
        ).length +
        filteredTraditionalLTCQuotes.filter(
          quote => underwritingResults.get(quote.traditionalLTCQuoteId) === 'ineligible',
        ).length,
    }),
    [adjustedQuotes, filteredTraditionalLTCQuotes, underwritingResults],
  );

  const filteredAdjustedQuotes = useMemo(() => {
    if (isLoading || selectedEligibilityFilters.length === 0) {
      return adjustedQuotes;
    }

    return adjustedQuotes.filter(quote => {
      const status = underwritingResults.get(quote.insuranceQuoteId);
      if (!status) return true; // Show quotes without status
      return selectedEligibilityFilters.includes(status);
    });
  }, [adjustedQuotes, underwritingResults, isLoading, selectedEligibilityFilters]);

  const filteredTraditionalLTCQuotesWithEligibility = useMemo(() => {
    if (isLoading || selectedEligibilityFilters.length === 0) {
      return filteredTraditionalLTCQuotes;
    }

    return filteredTraditionalLTCQuotes.filter(quote => {
      const status = underwritingResults.get(quote.traditionalLTCQuoteId);
      if (!status) return true; // Show quotes without status
      return selectedEligibilityFilters.includes(status);
    });
  }, [filteredTraditionalLTCQuotes, underwritingResults, isLoading, selectedEligibilityFilters]);

  const hasValidUnderwritingResults = Array.from(underwritingResults.values()).some(
    result => result !== null,
  );

  const showEligibilityFilter = !isLoading && hasValidUnderwritingResults;

  return {
    underwritingResults,
    eligibilityCounts,
    filteredAdjustedQuotes,
    filteredTraditionalLTCQuotesWithEligibility,
    showEligibilityFilter,
    isLoading,
  };
}
